import { ILocationState } from '@AppModels/.';
import * as AppState from '@AppModels/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	IContact,
	ICreateEntityRichContentCommand,
	IEntity,
	IOperationResultNoValue,
	QueryCommands,
} from '@ViewModels';
import { NotFound } from '@WebComponents/NotFound';
import { StyleDeclarationValue, css } from 'aphrodite';
import Bluebird from 'bluebird';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { parse as queryStringToObject } from 'query-string';
import * as React from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { ContactViewModel, EntityViewModel, RichContentComposerViewModel } from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { AppDrawerFabButton } from '../../components-v2/Drawer/AppDrawerFab';
import { THEME } from '../../components-v2/Theme/theme';
import { AutomationSelectorModal } from '../../components/automation/AutomationSelector';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../components/LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { AutomationsIcon } from '../../components/svgs/icons/AutomationsIcon';
import { ContactActivity, IContactActivityComponent } from '../People/Contact/Activity';
import { ContactInfo } from '../PeopleV2/PeopleContainer/AppDrawerContents/ContactInfo/ContactInfo';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

export interface IContactProps
	extends AppState.IUserSessionComponentProps,
		RouteComponentProps<any>,
		IEventLoggingComponentProps,
		AppState.IErrorMessageComponentProps,
		AppState.IActionItemComposerComponentProps,
		AppState.IFabComponentProps,
		AppState.INoteComposerComponentProps,
		INotificationServiceComponentProps<ContactViewModel>,
		AppState.IFullscreenModalComponentProps {
	className?: string;
	contact?: ContactViewModel;
	contactId?: string;
	disableCompanyLink?: boolean;
	hideDeleteButton?: boolean;
	hideEditButton?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IState {
	contact?: ContactViewModel;
	createRichContentQueryCommand?: ICreateEntityRichContentCommand;
	loadError?: IOperationResultNoValue;
	redirectPath?: string;
	showInitAutomationsModal?: boolean;
	showNotFound?: boolean;
	shouldPollForRelationshipHealth?: boolean;
}

class _Contact extends React.Component<IContactProps, IState> {
	private mMounted: boolean;
	private contactActivityComponentRef: IContactActivityComponent;
	private mInProgressEntityResolvePromise: Bluebird<EntityViewModel<IEntity>[]>;

	public static getDerivedStateFromProps(props: IContactProps, state: IState) {
		const nextState: IState = {};
		if (!!props.contact && props.contact !== state.contact) {
			nextState.contact = props.contact;
		} else {
			const currentContactId = state.contact ? state.contact.id : null;
			const locationState: ILocationState<ContactViewModel, IContact> =
				(props.history.location ? props.history.location.state : null) || {};
			const nextContactId = props.match.params?.id || props.contactId;
			if (!!locationState && !!locationState.viewModel) {
				if (state.contact !== locationState.viewModel) {
					nextState.contact = locationState.viewModel;
				}
			} else if (nextContactId && currentContactId !== nextContactId) {
				nextState.contact = new ContactViewModel(props.userSession, {
					id: nextContactId,
				});
			}
		}

		if (nextState.contact) {
			// clear the error
			nextState.showNotFound = false;
			nextState.loadError = null;
		}

		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	constructor(props: IContactProps) {
		super(props);

		makeObservable<_Contact, 'mMounted' | 'isLoading'>(this, {
			mMounted: observable,
			init: action,
			isLoading: computed,
		});

		this.state = {
			...(_Contact.getDerivedStateFromProps(props, {}) || {}),
		};
	}

	init() {
		this.mMounted = true;
		const { history, userSession } = this.props;
		const { contact } = this.state;

		const promise = this.loadContact();
		// handle query commands
		const searchParams = queryStringToObject(history?.location?.search || '');
		const cmd = searchParams?.cmd;
		switch (cmd) {
			case 'start-automation': {
				promise?.then(() => {
					history.replace({
						pathname: history.location.pathname,
						state: history.location.state,
					});
					if (userSession.account.features?.automation?.enabled && userSession.featurePermissions?.automation?.read) {
						this.showInitAutomationsModal();
					}
				});
				break;
			}
			case 'manage-automation': {
				promise?.then(() => {
					history.replace({
						pathname: history.location.pathname,
						state: history.location.state,
					});
					const automationId = searchParams?.automationId;
					if (
						!!automationId &&
						userSession.account.features?.automation?.enabled &&
						!!contact?.inProgressAutomations?.find(x => x.automationId === automationId)
					) {
						this.contactActivityComponentRef?.showRichContentTab({
							manageAutomationId: automationId,
						});
					}
				});
				break;
			}
			default: {
				break;
			}
		}
	}

	public componentDidMount() {
		this.init();
	}

	public componentWillUnmount() {
		this.mMounted = false;
		if (this.mInProgressEntityResolvePromise) {
			this.mInProgressEntityResolvePromise.cancel();
			this.mInProgressEntityResolvePromise = null;
		}
	}

	public componentDidUpdate() {
		const { contact, showNotFound, loadError } = this.state;
		if (!this.isLoading && !contact.isLoaded && !showNotFound && !loadError) {
			this.loadContact();
		}
	}

	private setShouldStartPollingForRelationshipUpdate = (shouldPoll: boolean) => {
		this.setState({ shouldPollForRelationshipHealth: shouldPoll });
	};

	public render() {
		const { className, styles, userSession } = this.props;
		const { contact, redirectPath, showNotFound, showInitAutomationsModal } = this.state;
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} contact-container ${className || ''}`}>
				<MultiContainerHeader
					appBarHeader={!!contact && !!contact.name && <NavigationBreadcrumbsBar currentLocationName={contact.name} />}
					fullscreenHeader='Contact Profile'
				/>
				{showNotFound ? (
					<NotFound
						className={css(styleSheet.notFound)}
						message='The contact you are looking for magically vanished.'
					/>
				) : (
					<>
						<div className={css(styleSheet.splitMaster)}>
							<ContactInfo contactId={contact.id} />
						</div>
						<div className={css(styleSheet.splitDetail)}>{this.onRenderSplitViewDetail()}</div>
					</>
				)}
				{!!redirectPath && <Redirect push={true} to={redirectPath} />}
				{showInitAutomationsModal ? (
					<AutomationSelectorModal
						contact={contact as unknown as IContact}
						createAutomationForContact={true}
						closeModal={() => this.onAutomationSelectorModalRequestClose()}
					/>
				) : null}
				<AppDrawer>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerToolbar
						extendedFabButtons={
							<>
								{userSession.account?.features?.automation?.enabled &&
								userSession.featurePermissions?.automation?.read ? (
									<AppDrawerFabButton
										tooltipText='Start Automation'
										icon={<AutomationsIcon fillColor={THEME.colors.icon.primary} />}
										onClick={() => this.showInitAutomationsModal()}
									/>
								) : null}
							</>
						}
					>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					</AppDrawerToolbar>
				</AppDrawer>
			</div>
		);
	}

	private onAutomationSelectorModalRequestClose = () => {
		this.setState({
			showInitAutomationsModal: false,
		});
	};

	private get isLoading() {
		const { contact } = this.state;
		return !contact || (!!contact && !!contact.isLoading) || !this.mMounted;
	}

	private showInitAutomationsModal = () => {
		this.setState({
			showInitAutomationsModal: true,
		});
	};

	private loadContact = (contact: ContactViewModel = this.state.contact) => {
		if (contact) {
			const { history, logEvent, noteComposer, logApiError, actionItemComposer } = this.props;
			const promise = contact.load(true);
			if (promise) {
				logEvent('ContactLoad');
				if (history) {
					history.location.state = null;
				}

				// parse query commands for this contact
				const nextState = this.getNextStateWithQueryCommand();
				if (nextState) {
					this.setState(nextState);
				}

				promise
					.then(() => {
						if (this.mMounted) {
							const { createRichContentQueryCommand } = this.state;

							// handle pending query commands
							if (createRichContentQueryCommand) {
								// remove the command
								history.replace(history.location.pathname);
								this.setState({
									createRichContentQueryCommand: null,
								});

								if (this.mInProgressEntityResolvePromise) {
									this.mInProgressEntityResolvePromise.cancel();
								}

								// FOLLOWUP: Resolve

								// @ts-ignore
								const composer: RichContentComposerViewModel =
									createRichContentQueryCommand.type === 'note' ? noteComposer : actionItemComposer;
								const loadPromise = composer.showWithReferencedEntities(
									[contact],
									createRichContentQueryCommand.refs.contacts,
									createRichContentQueryCommand.refs.companies
								);
								if (loadPromise) {
									logEvent('RichContentQueryCommandReferences-Load');
									this.mInProgressEntityResolvePromise = loadPromise;
									const onFinish = () => {
										if (this.mInProgressEntityResolvePromise === loadPromise) {
											this.mInProgressEntityResolvePromise = null;
										}
									};
									this.mInProgressEntityResolvePromise.then(onFinish).catch((error: IOperationResultNoValue) => {
										logApiError('RichContentQueryCommandReferences-Error', error);
										onFinish();
									});
								}
							}

							if (history) {
								// save it back to location state
								const locationState: ILocationState<ContactViewModel, IContact> = {
									viewModel: contact,
								};
								history.location.state = locationState;
							}
						}
					})
					.catch((error: IOperationResultNoValue) => {
						logApiError('ContactLoad-Error', error);
						if (this.mMounted) {
							const stateWithError: IState = { loadError: error };
							if (error.systemCode === 404) {
								stateWithError.showNotFound = true;
							}
							this.setState(stateWithError);
						}
					});
			}
			return promise;
		}
	};

	private onRenderSplitViewDetail() {
		const { contact } = this.state;
		const propsWithoutClassName = { ...this.props, ...{ className: null } };
		return (
			<ContactActivity
				{...propsWithoutClassName}
				contact={contact}
				innerRef={this.onContactActivityRef}
				setShouldStartPollingForRelationshipUpdate={this.setShouldStartPollingForRelationshipUpdate}
			/>
		);
	}

	private getNextStateWithQueryCommand = () => {
		const nextState: IState = {};
		const { location } = this.props;
		if (location) {
			const queryCommand = QueryCommands.Contact.parse(location.search);
			if (queryCommand) {
				switch (queryCommand.command) {
					case 'crc': {
						nextState.createRichContentQueryCommand = queryCommand as ICreateEntityRichContentCommand;
						break;
					}
					default: {
						break;
					}
				}
			}
		}
		return Object.keys(nextState).length > 0 ? nextState : null;
	};

	private onContactActivityRef = (ref?: IContactActivityComponent) => {
		this.contactActivityComponentRef = ref;
	};
}

// FOLLOWUP: Resolve

// @ts-ignore
const ContactAsObserver = observer(_Contact);
const ContactWithContext = inject(
	AppState.ActionItemComposerViewModelKey,
	AppState.ErrorMessagesViewModelKey,
	AppState.NoteComposerViewModelKey,
	AppState.UserSessionViewModelKey,
	AppState.FullScreenModalViewModelKey
)(ContactAsObserver);
const ContactWithNotifications = withEventLogging(ContactWithContext, 'Contact');
export const Contact = withRouter(withNotificationService(ContactWithNotifications));
